import React from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  GridItem,
  Text,
  Flex,
  Circle,
  AbsoluteCenter,
  Button,
  VStack,
} from '@chakra-ui/react';

import { DeptGrid } from '@rsc/ui-components/dept/design-system';
import Link from '@rsc/ui-components/tailwind/Link';
import getLink, {
  getCollectionLink,
} from '@rsc/utils/getLink';
import Image from '@rsc/ui-components/tailwind/Image';
import SanityBackgroundImage from '../../../atoms/SanityBackgroundImage';
import { generateSectionId } from '../../../utils/generateSectionId';

function CollectionProductCategories({ block }) {
  if (!block.collections) return null;
  const { title, collections, cta } = block;

  return (
    <Container
      as={Flex}
      my={14}
      mb={10}
      justify="space-between"
      align="center"
      flexWrap="wrap"
      {...generateSectionId(block)}
    >
      <Text
        textStyle="h2"
        as="h2"
        maxW={{
          base: 'none',
          lg: '"39rem',
        }}
      >
        {title}
      </Text>
      {cta?.text && cta?.link && (
        <Button
          mt="6"
          variant="primary"
          as={Link}
          link={cta?.link}
          w={{
            base: 'full',
            md: 'auto',
          }}
          order={{
            base: 1,
            md: 0,
          }}
        >
          {cta.text}
        </Button>
      )}

      <DeptGrid my={10} flexBasis="100%">
        {collections.map(c => {
          if (!c.collectionReference) return null;
          return (
            <GridItem colSpan={2} key={JSON.stringify(c)}>
              <VStack
                as={Link}
                className="flex flex-col items-center relative gap-2 hover:text-secondary"
                link={
                  getLink(c.cta?.linkType, c.cta?.href, c.cta?.internalLink) ||
                  getCollectionLink(c.collectionReference?.store?.title)
                }
                _hover={{
                  '.icon': {
                    lg: {
                      filter:
                        'invert(98%) sepia(100%) saturate(3463%) hue-rotate(356deg) brightness(106%) contrast(105%)',
                    },
                  },
                  '.circle': {
                    lg: {
                      bg: 'brand.ink',
                    },
                  },
                }}
              >
                {c.icon && (
                  <AbsoluteCenter
                    as={Circle}
                    p={{ base: 0, lg: 2 }}
                    bg={{ base: 'transparent', lg: 'brand.sunny' }}
                    axis={{ lg: 'horizontal' }}
                    bottom={{ base: 'calc(50% - 18px)', lg: 6 }}
                    zIndex={1}
                    className="circle"
                    sx={{
                      img: {
                        base: {
                          filter:
                            'invert(98%) sepia(100%) saturate(3463%) hue-rotate(356deg) brightness(106%) contrast(105%)',
                        },
                        lg: { filter: 'none !important' },
                      },
                    }}
                  >
                    <Image
                      className="icon"
                      sanityImage={c.icon}
                      srcSetBasisWidth={100}
                      width="32px"
                    />
                  </AbsoluteCenter>
                )}

                <SanityBackgroundImage
                  w="full"
                  h="full"
                  filter={{
                    base: 'brightness(.5)',
                    lg: 'none',
                  }}
                  aspectRatio={1}
                  image={c.media?.image ?? c.collectionReference?.image}
                  maxImageWidth={600}
                  borderRadius="lg"
                  transition="filter, transform .1s ease-in-out"
                  _hover={{
                    lg: {
                      filter: 'brightness(.6)',
                      transform: 'scale(1.02)',
                    },
                  }}
                />
              </VStack>
              <Text fontWeight="semibold" noOfLines={2} mt="2">
                {c.title || c.collectionReference?.store?.title}
              </Text>
            </GridItem>
          );
        })}
      </DeptGrid>
    </Container>
  );
}

CollectionProductCategories.propTypes = {
  block: PropTypes.shape({
    title: PropTypes.string.isRequired,
    cta: PropTypes.shape({
      text: PropTypes.string,
      link: PropTypes.shape({
        linkType: PropTypes.string,
        href: PropTypes.string,
        internalLink: PropTypes.shape({}),
      }),
    }),
    collections: PropTypes.arrayOf(
      PropTypes.shape({
        collectionReference: PropTypes.shape({
          store: PropTypes.shape({
            slug: PropTypes.shape({
              current: PropTypes.string,
            }),
          }),
          slug: PropTypes.shape({
            current: PropTypes.string,
          }),
          title: PropTypes.string,
          media: PropTypes.shape({
            image: PropTypes.shape({
              asset: PropTypes.shape({
                url: PropTypes.string,
              }),
              alt: PropTypes.string,
            }),
          }),
          icon: PropTypes.shape({
            asset: PropTypes.shape({
              url: PropTypes.string,
            }),
          }),
        }),
      })
    ).isRequired,
  }).isRequired,
};

export default CollectionProductCategories;
